'use client'

import { Builder } from '@builder.io/react'
import type { Input } from '@builder.io/sdk'

import { TopNavV5 } from './TopNavV5'
import { defaultTiles, defaultTopNavV5Props, supplementsSubMenu } from './TopNavV5.defaults'

const stampHelperText = 'Optional stamp (ex: New! , Featured, Sale, etc.)'

const TITLE: Input = {
  name: 'title',
  type: 'text',
  defaultValue: 'Programs',
  localized: true,
}
const STAMP: Input = {
  name: 'stamp',
  type: 'text',
  localized: true,
  helperText: stampHelperText,
  defaultValue: 'NEW',
}

const linkSubFields: Input[] = [
  {
    name: 'href',
    type: 'text',
    localized: true,
  },
  {
    name: 'newTab',
    friendlyName: 'Open in a new tab',
    type: 'boolean',
  },
]

const subMenuFields: Input[] = [
  TITLE,
  STAMP,
  {
    name: 'isLink',
    type: 'boolean',
    defaultValue: false,
  },
  {
    name: 'link',
    type: 'object',
    showIf: `options.get('isLink') === true`,
    subFields: [...linkSubFields],
  },
  {
    name: 'hasCtaMobile',
    type: 'boolean',
  },
  {
    name: 'ctaMobile',
    showIf: `options.get('hasCtaMobile') === true`,
    type: 'object',
    subFields: [TITLE, ...linkSubFields],
  },
  {
    name: 'ctaDesktop',
    type: 'object',
    subFields: [TITLE, ...linkSubFields],
  },
  {
    name: 'tiles',
    type: 'list',
    subFields: [
      TITLE,
      STAMP,
      ...linkSubFields,
      {
        name: 'image',
        type: 'file',
        allowedFileTypes: ['jpeg', 'png', 'webp'],
        localized: true,
      },
      {
        name: 'alt',
        type: 'text',
        localized: true,
      },
      {
        name: 'isVisible',
        type: 'boolean',
        localized: true,
      },
    ],
    defaultValue: defaultTiles,
  },
  {
    name: 'items',
    type: 'list',
    subFields: [
      TITLE,
      STAMP,
      {
        name: 'isLink',
        type: 'boolean',
      },
      {
        name: 'link',
        type: 'object',
        showIf: `options.get('isLink') === true`,
        subFields: [...linkSubFields],
      },
      {
        name: 'links',
        type: 'list',
        subFields: [TITLE, STAMP, ...linkSubFields],
      },
    ],
    defaultValue: supplementsSubMenu.items,
  },
]

Builder.registerComponent(TopNavV5, {
  name: 'TopNavV5',
  friendlyName: 'TopNav V5',
  inputs: [
    {
      name: 'account',
      type: 'object',
      subFields: [
        {
          name: 'title', // logged in title
          type: 'string',
          friendlyName: 'Logged in title',
          defaultValue: defaultTopNavV5Props.account?.title,
        },
        {
          name: 'loggedOutTitle',
          type: 'string',
          defaultValue: defaultTopNavV5Props.account?.loggedOutTitle,
        },
        {
          name: 'loggedOutSubtitle',
          type: 'string',
          defaultValue: defaultTopNavV5Props.account?.loggedOutSubtitle,
        },
        {
          name: 'welcomeCopy',
          type: 'string',
          defaultValue: defaultTopNavV5Props.account?.welcomeCopy,
          helperText:
            'Displayed under "Account" for logged in users. If the user has a name set, it will be displayed as: "Welcome back, first_name last_name.". If no name is set or if name is "guest" it will displayed: "Welcome back!".',
        },
        {
          name: 'signOutCopy',
          type: 'string',
          defaultValue: defaultTopNavV5Props.account?.signOutCopy,
          helperText: 'Copy for the sign out button.',
        },
        {
          name: 'logInCopy',
          type: 'text',
          defaultValue: defaultTopNavV5Props.account?.logInCopy,
        },
        {
          name: 'links',
          type: 'list',
          subFields: [TITLE, ...linkSubFields],
          defaultValue: defaultTopNavV5Props.account?.links,
          helperText: 'List of links displayed when users are logged in or logged out.',
        },
        {
          name: 'bodiCta',
          type: 'object',
          subFields: [TITLE, ...linkSubFields],
          defaultValue: defaultTopNavV5Props.account?.bodiCta,
          helperText:
            'Displayed as a button next to the account icon. If the user is logged in and already has BODi, this will be hidden.',
        },
        {
          name: 'loggedInLinks',
          type: 'list',
          subFields: [
            TITLE,
            ...linkSubFields,
            {
              name: 'imageUrl',
              type: 'file',
              allowedFileTypes: ['jpeg', 'png', 'webp'],
            },
            {
              name: 'imageAlt',
              type: 'text',
            },
            {
              name: 'subtitle',
              type: 'string',
            },
          ],
          defaultValue: defaultTopNavV5Props.account?.loggedInLinks,
        },
      ],
      defaultValue: defaultTopNavV5Props.account,
    },
    {
      name: 'logo',
      type: 'object',
      subFields: [
        {
          name: 'src',
          type: 'file',
          friendlyName: 'Logo image',
        },
        {
          name: 'href',
          type: 'string',
          friendlyName: 'Logo link',
        },
        {
          name: 'altText',
          type: 'string',
          friendlyName: 'Logo image alt text',
        },
        {
          name: 'desktopWidth',
          type: 'number',
        },
        {
          name: 'desktopHeight',
          type: 'number',
        },
        {
          name: 'mobileWidth',
          type: 'number',
        },
        {
          name: 'mobileHeight',
          type: 'number',
        },
      ],
      defaultValue: defaultTopNavV5Props.logo,
    },
    {
      name: 'items',
      friendlyName: 'Menu Items',
      type: 'list',
      subFields: [
        TITLE,
        STAMP,
        {
          name: 'subMenuType',
          type: 'string',
          enum: ['subMenu', 'leftMenu', 'miniMenu', 'link'],
          defaultValue: 'subMenu',
        },
        {
          name: 'link',
          type: 'object',
          showIf: `options.get('subMenuType') === 'link'`,
          subFields: [
            {
              name: 'href',
              type: 'text',
              localized: true,
            },
            {
              name: 'newTab',
              friendlyName: 'Open in a new tab',
              type: 'boolean',
            },
          ],
        },
        {
          name: 'subMenu',
          friendlyName: 'Sub menu items',
          type: 'object',
          showIf: `options.get('subMenuType') === 'subMenu'`,
          subFields: subMenuFields,
        },
        {
          name: 'leftMenuItems',
          friendlyName: 'Left menu items',
          type: 'list',
          showIf: `options.get('subMenuType') === 'leftMenu'`,
          subFields: subMenuFields,
        },
        {
          name: 'miniMenuItems',
          friendlyName: 'Mini menu items',
          type: 'list',
          showIf: `options.get('subMenuType') === 'miniMenu'`,
          subFields: [
            {
              ...TITLE,
              type: 'richText',
            },
            STAMP,
            ...linkSubFields,
          ],
        },
      ],
      defaultValue: defaultTopNavV5Props.items,
    },
    {
      name: 'mobileFooterLinks',
      type: 'list',
      subFields: [TITLE, ...linkSubFields],
      defaultValue: defaultTopNavV5Props.mobileFooterLinks,
    },
  ],
  image: 'https://unpkg.com/@tabler/icons@2.44.0/icons/layout-navbar.svg',
})
